// angular import
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { NavigationItem } from '../navigation';
import { Location } from '@angular/common';
import { environment } from 'src/environments/environment';
import { ConfigurationsService } from '../../configuration/service/configurations.service';
import { TfService } from 'src/app/tf/tf-service.service';
import { Router } from '@angular/router';
import { CpCompanyService } from 'src/app/admin/tf-admin/tf-configuration/tf-company/service/cp-company.service';
import { PermissionCheckService } from 'src/app/Theme/shared/service/permission-check.service';
import { LanguageService } from 'src/app/multi-language/language.service';
import { Subscription } from 'rxjs';
import type { NavigationItem as NavItem } from '../navigation';

function collectNavItems(items: NavItem[] = []): NavItem[] {
  return items.flatMap((item) => {
    if (item?.type === 'group' && item.children?.length) {
      return collectNavItems(item.children as NavItem[]);
    }
    return item ? [item] : [];
  });
}

/** Collect permission codes from nav items that have a permission for the given role (single source of truth: navigation config). */
function getNavPermissionCodesForRole(navigation: any, role: 'admin' | 'contact'): string[] {
  const codes = collectNavItems(navigation ?? [])
    .filter((i: NavItem) => i.role === role && i.permission)
    .map((i: NavItem) => i.permission as string);
  return Array.from(new Set(codes));
}

@Component({
  selector: 'app-nav-content',
  templateUrl: './nav-content.component.html',
  styleUrls: ['./nav-content.component.scss']
})
export class NavContentComponent implements OnInit, AfterViewInit, OnDestroy {
  // public method
  // version
  title = 'Zum Connect';
  currentApplicationVersion = environment.appVersion;
  config: { layout?: string } = { layout: 'vertical' };
  navigation: any;
  prevDisabled: string;
  nextDisabled: string;
  contentWidth: number;
  wrapperWidth: any;
  scrollWidth: any;
  windowWidth: number;
  themeConfig;

  @Output() onNavCollapsedMob = new EventEmitter();
  @ViewChild('navbarContent', { static: false }) navbarContent: ElementRef;
  @ViewChild('navbarWrapper', { static: false }) navbarWrapper: ElementRef;
  logoImageUrl: string;
  userGroups: string[] = [];
  /** Resolved permissions for nav items (contact or admin); used to hide permission-gated items when not allowed. */
  contactNavPermissions: Record<string, boolean> | null = null;

  showSidebarLanguage = true;
  languageList: Array<{ code: string; name?: string; status?: string }> = [];
  selectedLanguage = 'en';
  isLangDropdownOpen = false;
  private languageSub?: Subscription;
  private languageUpdateHandler = () => this.loadLanguages();

  // constructor
  constructor(public nav: NavigationItem,
    private location: Location,
    private ConfigurationsService: ConfigurationsService,
    private tfService: TfService,
    private router: Router,
    private cpCompanyService: CpCompanyService,
    private permissionCheck: PermissionCheckService,
    private languageService: LanguageService
  ) {
    this.windowWidth = window.innerWidth;
    this.navigation = this.nav.get();
    this.prevDisabled = 'disabled';
    this.nextDisabled = '';
    this.scrollWidth = 0;
    this.contentWidth = 0;
  }

  // life cycle event
  ngOnInit() {
    this.ConfigurationsService.getConfig().subscribe((res) => {
      this.themeConfig = res;
      this.config = { ...this.themeConfig };
      if (this.windowWidth <= 430) {
        this.themeConfig.layout = 'vertical';
        setTimeout(() => {
          document.querySelector('.pcoded-navbar')?.classList.add('menupos-static');
          const navScroll = document.querySelector('#nav-ps-datta') as HTMLElement | null;
          if (navScroll) {
            navScroll.style.maxHeight = '100%';
          }
        }, 500);
      }
    });

    // Subscribe to the logo URL from the service
    this.cpCompanyService.pictureUrlLogo$.subscribe(url => {
      if (url) {
        this.logoImageUrl = url;
      }
    });

    // Only fetch from API if the current value is null
    if (!this.cpCompanyService['pictureUrlSourceLogo'].getValue()) {
      this.cpCompanyService.getCompany().subscribe(
        (response: any) => {
          const logoUrl = response.logo_url || 'assets/images/logo.png';
          this.logoImageUrl = logoUrl;
          this.cpCompanyService.updateLogoUrl(logoUrl);
          const favIconUrl = response.favIcon;
          if (favIconUrl) {
            this.cpCompanyService.updateFavIconUrl(favIconUrl);
          }
        },
        (error) => console.log(error)
      );
    }

    this.tfService.getUserGroups().subscribe(groups => {
      this.userGroups = groups || [];
      const role = this.userGroups.includes('admin')
        ? 'admin'
        : this.tfService.isCustomerPortalUser(this.userGroups)
          ? 'contact'
          : null;
      if (role) {
        const permissionCodes = getNavPermissionCodesForRole(this.navigation, role);
        if (permissionCodes.length) {
          this.permissionCheck.checkPermissionsList(permissionCodes).subscribe(result => {
            this.contactNavPermissions = result;
          });
        }
      }
    });

    this.selectedLanguage = this.languageService.getCurrentLanguage();
    this.loadLanguages();
    window.addEventListener('languageListUpdated', this.languageUpdateHandler);
    this.languageSub = this.languageService.currentLanguage$.subscribe((lang) => {
      this.selectedLanguage = this.languageService.normalizeLanguageCode(lang) || 'en';
    });
  }

  ngOnDestroy(): void {
    window.removeEventListener('languageListUpdated', this.languageUpdateHandler);
    this.languageSub?.unsubscribe();
  }

  /** Phone band from `_fluid-scale.scss` `$fluid-vp-max` (430px). */
  get isPhoneViewport(): boolean {
    return this.windowWidth <= 430;
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.windowWidth = window.innerWidth;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (this.isLangDropdownOpen && !target?.closest('.sidebar-language')) {
      this.isLangDropdownOpen = false;
    }
  }

  toggleLangDropdown(event: Event): void {
    event.stopPropagation();
    if (this.isIconRail) {
      return;
    }
    this.isLangDropdownOpen = !this.isLangDropdownOpen;
    if (this.isLangDropdownOpen) {
      this.loadLanguages();
    }
  }

  changeSiteLanguage(code: string, event?: Event): void {
    event?.stopPropagation();
    const normalized = this.languageService.normalizeLanguageCode(code) || code;
    this.selectedLanguage = normalized;
    this.languageService.setLanguage(normalized);
    this.isLangDropdownOpen = false;
  }

  getLanguageDisplayName(code: string): string {
    const names: Record<string, string> = {
      en: 'English',
      fr: 'Français',
      nl: 'Nederlands'
    };
    const lower = (code || '').toLowerCase();
    return names[lower] || this.getLanguageNameKey(code);
  }

  getLanguageNameKey(code: string): string {
    if (!code) {
      return '';
    }
    const lower = code.toLowerCase();
    if (lower === 'en') {
      return 'English';
    }
    if (lower === 'fr') {
      return 'French';
    }
    if (lower === 'nl') {
      return 'Dutch';
    }
    return code;
  }

  isLanguageSelected(code: string): boolean {
    return this.languageService.normalizeLanguageCode(code) === this.selectedLanguage?.toLowerCase();
  }

  get activeLanguages(): Array<{ code: string; name?: string; status?: string }> {
    return (this.languageList || []).filter((lang) => lang.status === 'active');
  }

  /** Icon-only desktop rail — language options should fly out like a tooltip. */
  get isIconRail(): boolean {
    const navbar = document.querySelector('.pcoded-navbar');
    return !!(navbar && navbar.classList.contains('navbar-collapsed') && !navbar.classList.contains('mob-open'));
  }

  private loadLanguages(): void {
    this.languageService.searchLanguage('').subscribe((res) => {
      this.languageList = res || [];
      if (!this.languageList.length) {
        return;
      }
      const current = this.languageService.getCurrentLanguage();
      const currentActive = this.languageList.find((lang) =>
        lang.code?.toLowerCase() === current && lang.status === 'active'
      );
      if (!currentActive) {
        const firstActive = this.languageList.find((lang) => lang.status === 'active');
        if (firstActive?.code) {
          this.changeSiteLanguage(firstActive.code);
        }
      }
    });
  }

  ngAfterViewInit() {
    const layout = this.config?.layout ?? this.themeConfig?.layout ?? 'vertical';
    if (layout === 'horizontal' && this.navbarContent?.nativeElement && this.navbarWrapper?.nativeElement) {
      this.contentWidth = this.navbarContent.nativeElement.clientWidth;
      this.wrapperWidth = this.navbarWrapper.nativeElement.clientWidth;
    }
  }

  // public method
  scrollPlus() {
    this.scrollWidth = this.scrollWidth + (this.wrapperWidth - 80);
    if (this.scrollWidth > this.contentWidth - this.wrapperWidth) {
      this.scrollWidth = this.contentWidth - this.wrapperWidth + 80;
      this.nextDisabled = 'disabled';
    }
    this.prevDisabled = '';
    (document.querySelector('#side-nav-horizontal') as HTMLElement).style.marginLeft = '-' + this.scrollWidth + 'px';
  }

  scrollMinus() {
    this.scrollWidth = this.scrollWidth - this.wrapperWidth;
    if (this.scrollWidth < 0) {
      this.scrollWidth = 0;
      this.prevDisabled = 'disabled';
    }
    this.nextDisabled = '';
    (document.querySelector('#side-nav-horizontal') as HTMLElement).style.marginLeft = '-' + this.scrollWidth + 'px';
  }

  fireLeave() {
    const sections = document.querySelectorAll('.pcoded-hasmenu');
    for (let i = 0; i < sections.length; i++) {
      sections[i].classList.remove('active');
      sections[i].classList.remove('pcoded-trigger');
    }

    let current_url = this.location.path();
    if (this.location['_baseHref']) {
      current_url = this.location['_baseHref'] + this.location.path();
    }
    const link = "a.nav-link[ href='" + current_url + "' ]";
    const element = document.querySelector(link);
    if (element !== null && element !== undefined) {
      let parent = element.parentElement;
      do {
        // Move up to the parent element in each iteration
        parent = parent.parentElement;
        if (parent && parent.classList.contains('pcoded-hasmenu')) {
          if (!parent.classList.contains('pcoded-trigger')) {
            parent.classList.add('pcoded-trigger');
          }
        }
      } while (parent);
    }
  }

  navMob() {
    if (this.windowWidth <= 430 && document.querySelector('app-navigation.pcoded-navbar').classList.contains('mob-open')) {
      this.onNavCollapsedMob.emit();
    }
  }

  fireOutClick() {
    let current_url = this.location.path();
    if (this.location['_baseHref']) {
      current_url = this.location['_baseHref'] + this.location.path();
    }
    const link = "a.nav-link[ href='" + current_url + "' ]";
    const element = document.querySelector(link);
    if (element !== null && element !== undefined) {
      let parent = element.parentElement;
      if (this.themeConfig.layout === 'vertical') {
        do {
          // Move up to the parent element in each iteration
          parent = parent.parentElement;
          if (parent && parent.classList.contains('pcoded-hasmenu')) {
            if (!parent.classList.contains('pcoded-trigger')) {
              parent.classList.add('pcoded-trigger');
            }
          }
        } while (parent);
        parent.classList.add('active');
      }
    }
  }
  logout() {
    // Use the service's logout method
    this.tfService.logout().subscribe({
      next: () => {
        // Navigate to signin page after successful logout
        this.router.navigateByUrl('/auth/signin');
      },
      error: (error) => {
        console.error('Logout failed', error);
        // Even on error, navigate to signin
        // this.router.navigateByUrl('/auth/signin');
      }
    });
  }
  redirect() {
    this.router.navigateByUrl('admin/configuration/settings-navigation');
  }

  // Show native title tooltip only when sidebar is collapsed
  get showTitle(): boolean {
    const navbar = document.querySelector('.pcoded-navbar');
    return !!(navbar && navbar.classList.contains('navbar-collapsed'));
  }
}
